import { Button, Chip } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { AccessCard } from "@/api/access-cards";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder.tsx";
import { FaTrash, FaPlus } from "react-icons/fa6";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes.ts";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";
import { useLocation, useNavigate } from "react-router-dom";
import { UserChip } from "@/app/components/chips/userChip";

export function AccessCardsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);

  const [cards, setCards] = useState<AccessCard[] | null>(null);

  const handleError = useErrorHandling();

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  const loadData = useCallback(() => {
    api.accessCards
      .list()
      .then((response) => {
        setCards(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [handleError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const deleteCard = useCallback(
    (cardId: number) => {
      if (!confirm("Вы уверены, что хотите удалить эту карту?")) {
        return;
      }

      api.accessCards
        .delete(cardId)
        .then(() => {
          toast.success("Карта успешно удалена");
          loadData();
        })
        .catch((err) => {
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        });
    },
    [handleError, loadData],
  );

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 md:min-w-[240px] w-full md:w-auto">
          {/* @ts-ignore */}
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow flex flex-col gap-4">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            <div className="flex flex-row justify-between items-center">
              <span className="font-medium text-lg">Выданные карты</span>
              <Button
                startContent={<FaPlus />}
                color="primary"
                size="sm"
                onClick={() => navigate("/dashboard/skud/issue-card")}>
                Выдать карту
              </Button>
            </div>
            <TableBuilder
              removeWrapper
              displayOptions={false}
              columns={[
                {
                  label: "ID",
                  key: "id",
                  type: ColumnType.Number,
                  sortable: true,
                },
                {
                  label: "Card UID",
                  key: "card_uid",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  label: "Тип",
                  key: "issuer_type",
                  type: ColumnType.String,
                  sortable: true,
                  render: (value: string) => (
                    <span className="text-xs font-medium">
                      {value === "BUSINESS" ? "Бизнес" : "Арендатор"}
                    </span>
                  ),
                },
                {
                  label: "Пользователь",
                  key: "user_id",
                  type: ColumnType.Custom,
                  sortable: true,
                  render: (_value: number, row: AccessCard) => {
                    if (row.issuer_type === "RENTER" && row.renter_id) {
                      return (
                        <Chip size="sm" variant="dot" color="warning">
                          Сотрудник #{row.user_id}
                        </Chip>
                      );
                    }
                    return <UserChip userId={row.user_id} />;
                  },
                },
                {
                  label: "Арендатор",
                  key: "renter_id",
                  type: ColumnType.Renter,
                  sortable: true,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaTrash />,
                      onClick: (_value, row) => {
                        deleteCard(row.id);
                      },
                    },
                  ],
                },
              ]}
              data={cards || []}
              rowsPerPage={10}
            />
          </div>
        </div>
      </div>
    </>
  );
}
